import { useEffect, useState } from 'react';
import { Chart } from 'react-chartjs-2';
import { ChartContainer, ChartsRow, ChartWrapper } from './chart-style';
import { chartOptionsQtd, chartOptionsTime } from './chart-config';
import { TaskChartData } from './chart-type';
import { ChartFilter } from '../filters/filter-type';
import { api } from 'src/services/index';
import { isArray } from 'util';

interface Props {
  filters: ChartFilter;
}

const CACHE_NAME = 'task-chart-cache';

function generateCacheKey(filters: ChartFilter) {
  const sorted = Object.keys(filters)
    .sort()
    .reduce((obj, key) => {
      obj[key] = (filters as any)[key];
      return obj;
    }, {} as any);
  return `chartData-${JSON.stringify(sorted)}`;
}

function resolveGroupLabel(
  groupBy: string,
  id: number,
  maps: {
    technicianMap?: Record<number, string>;
    sectorMap?: Record<number, string>;
    cellMap?: Record<number, string>;
    patrimonyMap?: Record<number, string>;
  } 
): string {
  switch (groupBy) {
    case 'technician':
      return maps.technicianMap?.[id] || `Técnico: ${id}`;
    case 'sector':
      return maps.sectorMap?.[id] || `Setor: ${id}`;
    case 'cell':
      return maps.cellMap?.[id] || `Célula: ${id}`;
    case 'patrimony':
      return maps.patrimonyMap?.[id] || `Patrimônio: ${id}`;
    default:
      return `Desconhecido: ${id}`;
  }
}
// ...importações inalteradas...

export function ChartComponent({ filters }: Props) {
  const [chartDataQtd, setChartDataQtd] = useState<any>(null);
  const [chartDataTempo, setChartDataTempo] = useState<any>(null);
  const [totalOS, setTotalOS] = useState<number>(0);

  useEffect(() => {
    async function fetchData() {
      const cacheKey = generateCacheKey(filters);

      if ('caches' in window) {
        const cache = await caches.open(CACHE_NAME);
        const cachedResponse = await cache.match(cacheKey);
        if (cachedResponse) {
          try {
            const cachedJson = await cachedResponse.json();
            setChartDataQtd(cachedJson.chartQtd);
            setChartDataTempo(cachedJson.chartTempo);
            fetchAndCache(cacheKey, cache);
            return;
          } catch (err) {
            console.warn('Erro ao ler cache', err);
          }
        }

        await fetchAndCache(cacheKey, cache);
      }
    }

    async function fetchAndCache(cacheKey: string, cache: Cache) {
      try {
        const params = new URLSearchParams();
        Object.entries({
          ...filters,
          periodType: filters.periodType || 'finishedAt',
        }).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            params.append(key, String(value));
          }
        });

        const res = await api.get(`/tasks/chart/completed?${params.toString()}`);
        const data: TaskChartData[] = res.data;

        const [techRes, sectorRes, cellRes, patrimonyRes] = await Promise.all([
          api.get('/technicians'),
          api.get('/operators'),
          api.get('/sectors'),
          api.get('/cells'),
          api.get('/patrimonies'),
        ]);

        const techMap = isArray(techRes.data)
          ? Object.fromEntries(techRes.data.map((t: any) => [t.id, t.name]))
          : {};
        const sectorMap = isArray(sectorRes.data)
          ? Object.fromEntries(sectorRes.data.map((s: any) => [s.id, s.name]))
          : {};
        const cellMap = isArray(cellRes.data)
          ? Object.fromEntries(cellRes.data.map((c: any) => [c.id, c.name]))
          : {};
        const patrimonyMap = isArray(patrimonyRes.data)
          ? Object.fromEntries(patrimonyRes.data.map((p: any) => [p.id, p.number]))
          : {};

        const labels = data.map(d =>
          resolveGroupLabel(filters.groupBy, d.group_key, {
            technicianMap: techMap,
            sectorMap,
            cellMap,
            patrimonyMap,
          })
        );

        const values = data.map(d => Number(d.total) || 0);

        const totalOS = values.reduce((sum, v) => sum + v, 0);
        
        const parseTimeToMinutes = (timeStr: string): number => {
          const match = timeStr.match(/(?:(\d+)h)?\s*(?:(\d+)m)?/);
          if (!match) return 0;
          const hours = parseInt(match[1] || '0', 10);
          const minutes = parseInt(match[2] || '0', 10);
          return hours * 60 + minutes;
        };

        const averageTimes = data.map(d => parseTimeToMinutes(d.averageServiceTime || '0h 0m'));

        const chartQtd = {
          labels,
          datasets: [
            {
              label: 'Quantidade de OS',
              data: values,
              backgroundColor: '#66C1FE',
            },
          ],
        };

        const chartTempo = {
          labels,
          datasets: [
            {
              label: 'Tempo',
              data: averageTimes,
              backgroundColor: '#F9CA85',
            },
          ],
        };

        setChartDataQtd(chartQtd);
        setChartDataTempo(chartTempo);
        setTotalOS(totalOS);

        const responseToCache = new Response(JSON.stringify({ chartQtd, chartTempo }), {
          headers: { 'Content-Type': 'application/json' },
        });
        await cache.put(cacheKey, responseToCache);

      } catch (error) {
        console.error('Erro ao carregar gráfico', error);
      }
    }

    fetchData();
  }, [filters]);

  if (!chartDataQtd || !chartDataTempo) return <p>Carregando gráficos...</p>;

  return (
    <ChartContainer>
      <ChartsRow>
        <ChartWrapper>
          <Chart type="bar" data={chartDataQtd} options={chartOptionsQtd(totalOS)} />
        </ChartWrapper>
        <ChartWrapper>
          <Chart type="bar" data={chartDataTempo} options={chartOptionsTime} />
        </ChartWrapper>
      </ChartsRow>
    </ChartContainer>
  );
}

